import React, { useState, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import TeamStats from './components/TeamStats';
import FilterControls from './components/FilterControls';
import UserTable from './components/UserTable';
import UserCard from './components/UserCard';
import UserEditModal from './components/UserEditModal';
import WorkflowBuilder from './components/WorkflowBuilder';
import Icon from '../../components/AppIcon';


const TeamManagement = () => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'workflows'
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    role: '',
    status: ''
  });

  // Mock data for team members
  const [users, setUsers] = useState([
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@sofron.ai",
      role: "Marketing Director",
      department: "Marketing",
      status: "Active",
      lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      permissions: {
        canCreateContent: true,
        canReviewContent: true,
        canApproveContent: true,
        canManageUsers: true,
        canViewReports: true,
        canConfigureRules: false
      }
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@sofron.ai",
      role: "Compliance Officer",
      department: "Compliance",
      status: "Active",
      lastActivity: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
      permissions: {
        canCreateContent: false,
        canReviewContent: true,
        canApproveContent: true,
        canManageUsers: false,
        canViewReports: true,
        canConfigureRules: true
      }
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.rodriguez@sofron.ai",
      role: "Marketing Creator",
      department: "Marketing",
      status: "Active",
      lastActivity: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
      permissions: {
        canCreateContent: true,
        canReviewContent: false,
        canApproveContent: false,
        canManageUsers: false,
        canViewReports: true,
        canConfigureRules: false
      }
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@sofron.ai",
      role: "Legal Reviewer",
      department: "Legal",
      status: "Active",
      lastActivity: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      permissions: {
        canCreateContent: false,
        canReviewContent: true,
        canApproveContent: true,
        canManageUsers: false,
        canViewReports: true,
        canConfigureRules: true
      }
    },
    {
      id: 5,
      name: "Lisa Wang",
      email: "lisa.wang@sofron.ai",
      role: "Marketing Creator",
      department: "Marketing",
      status: "Pending",
      lastActivity: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      permissions: {
        canCreateContent: true,
        canReviewContent: false,
        canApproveContent: false,
        canManageUsers: false,
        canViewReports: false,
        canConfigureRules: false
      }
    },
    {
      id: 6,
      name: "James Wilson",
      email: "james.wilson@sofron.ai",
      role: "Compliance Officer",
      department: "Compliance",
      status: "Inactive",
      lastActivity: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
      permissions: {
        canCreateContent: false,
        canReviewContent: true,
        canApproveContent: true,
        canManageUsers: false,
        canViewReports: true,
        canConfigureRules: true
      }
    }
  ]);

  // Mock workflows data
  const [workflows, setWorkflows] = useState([
    {
      id: 1,
      name: "Standard Content Review",
      description: "Default workflow for marketing content review and approval",
      avgTime: "2-3",
      successRate: "96",
      steps: [
        {
          id: 1,
          name: "Initial Review",
          approverRole: "Compliance Officer",
          condition: "low",
          required: true
        },
        {
          id: 2,
          name: "Final Approval",
          approverRole: "Marketing Director",
          condition: "medium",
          required: true
        }
      ]
    },
    {
      id: 2,
      name: "High-Risk Content Review",
      description: "Enhanced workflow for high-risk financial content",
      avgTime: "4-6",
      successRate: "92",
      steps: [
        {
          id: 1,
          name: "Compliance Review",
          approverRole: "Compliance Officer",
          condition: "high",
          required: true
        },
        {
          id: 2,
          name: "Legal Review",
          approverRole: "Legal Reviewer",
          condition: "high",
          required: true
        },
        {
          id: 3,
          name: "Director Approval",
          approverRole: "Marketing Director",
          condition: "high",
          required: true
        }
      ]
    }
  ]);

  // Mock team statistics
  const teamStats = {
    totalUsers: users?.length,
    activeUsers: users?.filter(u => u?.status === 'Active')?.length,
    pendingApprovals: 8,
    workflowEfficiency: 94
  };

  // Current user for header
  const currentUser = {
    name: "Admin User",
    role: "System Administrator"
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/content-analysis-dashboard' },
    { label: 'Team Management' }
  ];

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = !filters?.search || 
        user?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesDepartment = !filters?.department || user?.department === filters?.department;
      const matchesRole = !filters?.role || user?.role === filters?.role;
      const matchesStatus = !filters?.status || user?.status === filters?.status;
      
      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });

    // Sort users
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'lastActivity') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [users, filters, sortBy, sortOrder]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      department: '',
      role: '',
      status: ''
    });
  };

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleSaveUser = (updatedUser) => {
    setUsers(prev => prev?.map(user => 
      user?.id === updatedUser?.id ? updatedUser : user
    ));
  };

  const handleResetPassword = (user) => {
    // Mock password reset functionality
    alert(`Password reset email sent to ${user?.email}`);
  };

  const handleToggleStatus = (user) => {
    const newStatus = user?.status === 'Active' ? 'Inactive' : 'Active';
    setUsers(prev => prev?.map(u => 
      u?.id === user?.id ? { ...u, status: newStatus } : u
    ));
  };

  const handleInviteUser = () => {
    setSelectedUser(null);
    setIsEditModalOpen(true);
  };

  const handleSaveWorkflow = (workflowData) => {
    if (workflowData?.id) {
      setWorkflows(prev => prev?.map(w => 
        w?.id === workflowData?.id ? workflowData : w
      ));
    } else {
      setWorkflows(prev => [...prev, { ...workflowData, id: Date.now() }]);
    }
  };

  const handleDeleteWorkflow = (workflowId) => {
    setWorkflows(prev => prev?.filter(w => w?.id !== workflowId));
  };

  const userCounts = {
    total: users?.length,
    filtered: filteredAndSortedUsers?.length
  };

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <Breadcrumb items={breadcrumbItems} className="mb-4" />
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Team Management</h1>
                <p className="text-muted-foreground mt-2">
                  Manage user roles, permissions, and approval workflows for your compliance team
                </p>
              </div>
              <Button
                variant="default"
                iconName="UserPlus"
                iconPosition="left"
                onClick={handleInviteUser}
              >
                Invite User
              </Button>
            </div>
          </div>

          {/* Team Statistics */}
          <div className="mb-8">
            <TeamStats stats={teamStats} />
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('users')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-micro ${
                    activeTab === 'users' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  Team Members
                </button>
                <button
                  onClick={() => setActiveTab('workflows')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-micro ${
                    activeTab === 'workflows' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  Approval Workflows
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'users' ? (
            <>
              {/* Filter Controls */}
              <div className="mb-6">
                <FilterControls
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  userCounts={userCounts}
                />
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-foreground">View:</span>
                  <div className="flex items-center bg-muted rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-micro ${
                        viewMode === 'table' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Table
                    </button>
                    <button
                      onClick={() => setViewMode('cards')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-micro ${
                        viewMode === 'cards' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Cards
                    </button>
                  </div>
                </div>
              </div>

              {/* Users Display */}
              {viewMode === 'table' ? (
                <UserTable
                  users={filteredAndSortedUsers}
                  onEdit={handleEditUser}
                  onResetPassword={handleResetPassword}
                  onToggleStatus={handleToggleStatus}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAndSortedUsers?.map(user => (
                    <UserCard
                      key={user?.id}
                      user={user}
                      onEdit={handleEditUser}
                      onResetPassword={handleResetPassword}
                      onToggleStatus={handleToggleStatus}
                    />
                  ))}
                </div>
              )}

              {filteredAndSortedUsers?.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or invite new team members
                  </p>
                  <Button
                    variant="default"
                    iconName="UserPlus"
                    iconPosition="left"
                    onClick={handleInviteUser}
                  >
                    Invite User
                  </Button>
                </div>
              )}
            </>
          ) : (
            <WorkflowBuilder
              workflows={workflows}
              onSaveWorkflow={handleSaveWorkflow}
              onDeleteWorkflow={handleDeleteWorkflow}
            />
          )}
        </div>
      </main>
      {/* User Edit Modal */}
      <UserEditModal
        user={selectedUser}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
      />
    </div>
  );
};

export default TeamManagement;